import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getCareGaps, getInterventions } from '../../services/careCoordinationService';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';

// Styled Components - Consistent with other pages
const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const programOptions = [
  'All',
  'ACO',
  'PCMH',
  'Preventive Care',
  'Behavioral Health',
  'Cancer Screening',
  'Medication Management', 
  'Chronic Disease',
  'CMS'
];

export default React.memo(function Compliance() {
  const [program, setProgram] = useState('All');
  const [provider, setProvider] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [careGaps, setCareGaps] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [providers, setProviders] = useState(['All']);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Tab state for Care Gaps / Interventions
  const [detailsTab, setDetailsTab] = useState(0);
  
  // Use ref to track if we're already fetching data
  const isFetchingRef = useRef(false);

  // Get user data including facility information
  const userData = useUserData();

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    program: program !== 'All' ? program : undefined,
    provider: provider !== 'All' ? provider : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined
  }), [program, provider, dateFrom, dateTo]);

  // Single useEffect to handle all API calls
  useEffect(() => {
    // Prevent duplicate API calls
    if (isFetchingRef.current) {
      console.log('Compliance: Skipping duplicate API call - already fetching');
      return;
    }

    // Don't fetch if we don't have facility access
    if (!userData.defaultFacility?.id) {
      console.log('Compliance: No facility access available, skipping data fetch');
      return;
    }

    const fetchData = async () => {
      console.log('Compliance: Fetching data with filters:', filters);
      isFetchingRef.current = true;
      setLoading(true);
      
      try {
        // Fetch all data in parallel to reduce API calls
        const facilityId = userData.defaultFacility?.id;
        const [careGapsData, interventionsData] = await Promise.all([
          getCareGaps(filters, facilityId),
          getInterventions(filters, facilityId)
        ]);

        // Ensure we always have arrays, even if API returns undefined/null
        const safeCareGaps = Array.isArray(careGapsData) ? careGapsData : [];
        const safeInterventions = Array.isArray(interventionsData) ? interventionsData : [];
        
        // Debug logging to see what we're actually receiving
        console.log('Compliance: Raw care gaps data:', careGapsData);
        console.log('Compliance: Safe care gaps array:', safeCareGaps);
        if (safeCareGaps.length > 0) {
          console.log('Compliance: First care gap structure:', safeCareGaps[0]);
        }
        
        setCareGaps(safeCareGaps);
        setInterventions(safeInterventions);

        // Update provider options from care gaps data
        if (safeCareGaps && Array.isArray(safeCareGaps)) {
          const uniqueProviders = Array.from(new Set(safeCareGaps.map(g => g.provider).filter(Boolean)));
          setProviders(['All', ...uniqueProviders]);
        }
        
        console.log('Compliance: Data fetched successfully');
      } catch (error) {
        console.error('Error fetching compliance data:', error);
        // Set empty arrays on error to prevent undefined errors
        setCareGaps([]);
        setInterventions([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [filters, userData.defaultFacility?.id]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleProgramChange = useCallback((newProgram) => {
    setProgram(newProgram);
    // Reset provider when program changes
    setProvider('All');
  }, []);

  const handleProviderChange = useCallback((newProvider) => {
    setProvider(newProvider);
  }, []);

  const handleDateFromChange = useCallback((newDateFrom) => {
    setDateFrom(newDateFrom);
  }, []);

  const handleDateToChange = useCallback((newDateTo) => {
    setDateTo(newDateTo);
  }, []);

  // Handle compliance data refresh
  const handleRefresh = useCallback(async () => {
    const facilityId = userData.defaultFacility?.id;
    if (!facilityId) return;
    
    try {
      setRefreshing(true);
      // Refresh the compliance data
      const [careGapsData, interventionsData] = await Promise.all([
        getCareGaps(filters, facilityId),
        getInterventions(filters, facilityId)
      ]);
      
      const safeCareGaps = Array.isArray(careGapsData) ? careGapsData : [];
      const safeInterventions = Array.isArray(interventionsData) ? interventionsData : [];
      
      setCareGaps(safeCareGaps);
      setInterventions(safeInterventions);
      
      console.log('Compliance data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing compliance data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [filters, userData.defaultFacility?.id]);

  // Handle tab change for details section
  const handleDetailsTabChange = useCallback((event, newValue) => {
    setDetailsTab(newValue);
  }, []);

  // Memoize the program options to prevent unnecessary re-renders
  const memoizedProgramOptions = useMemo(() => programOptions, []);
  const memoizedProviders = useMemo(() => providers, [providers]);

  // Calculate aggregate statistics for care gaps
  const careGapsSummary = useMemo(() => {
    if (!careGaps || careGaps.length === 0) return null;
    
    const totalGaps = careGaps.length;
    const overdueGaps = careGaps.filter(gap => {
      const dueDate = gap.dueDate || gap.date;
      return dueDate && new Date(dueDate) < new Date();
    }).length;
    
    const byCategory = careGaps.reduce((acc, gap) => {
      const category = gap.category || gap.gap || 'Uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
    
    const byProvider = careGaps.reduce((acc, gap) => {
      const provider = gap.provider || 'Unassigned';
      acc[provider] = (acc[provider] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: totalGaps,
      overdue: overdueGaps,
      overduePercentage: totalGaps > 0 ? Math.round((overdueGaps / totalGaps) * 100) : 0,
      byCategory,
      byProvider
    };
  }, [careGaps]);

  // Calculate aggregate statistics for interventions
  const interventionsSummary = useMemo(() => {
    if (!interventions || interventions.length === 0) return null;
    
    const totalInterventions = interventions.length;
    const completedInterventions = interventions.filter(interv => 
      interv.status === 'completed' || interv.status === 'Completed'
    ).length;
    
    const byType = interventions.reduce((acc, interv) => {
      const type = interv.type || 'Uncategorized';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    
    const byStatus = interventions.reduce((acc, interv) => {
      const status = interv.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      total: totalInterventions,
      completed: completedInterventions,
      completionRate: totalInterventions > 0 ? Math.round((completedInterventions / totalInterventions) * 100) : 0,
      byType,
      byStatus
    };
  }, [interventions]);

  return (
    <Box>
      <ModernCard>
        <CardContent>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Program</InputLabel>
              <Select
                value={program}
                label="Program"
                onChange={e => handleProgramChange(e.target.value)}
              >
                {memoizedProgramOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>Provider</InputLabel>
              <Select
                value={provider}
                label="Provider"
                onChange={e => handleProviderChange(e.target.value)}
              >
                {memoizedProviders.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="From"
              value={dateFrom ? new Date(dateFrom) : null}
              onChange={(date) => handleDateFromChange(date ? date.toISOString().split('T')[0] : '')}
              slotProps={{
                textField: { size: 'small' }
              }}
            />
            <DatePicker
              label="To"
              value={dateTo ? new Date(dateTo) : null}
              onChange={(date) => handleDateToChange(date ? date.toISOString().split('T')[0] : '')}
              slotProps={{
                textField: { size: 'small' }
              }}
            />
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ ml: 'auto' }}
            >
              {refreshing ? 'REFRESHING...' : 'REFRESH'}
            </Button>
          </Box>
          
          {loading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Loading compliance data...
              </Typography>
            </Box>
          )}
          
          {/* Compliance Overview Header */}
          {!loading && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Compliance and Metrics Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Monitor care gaps, interventions, and compliance metrics to ensure quality patient care delivery.
              </Typography>
              
              {/* Compliance Summary Cards */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                        {careGaps?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Care Gaps
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="error.main" sx={{ fontWeight: 700 }}>
                        {careGapsSummary?.overdue || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Overdue Gaps
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                        {interventions?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Interventions
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                        {interventionsSummary?.completionRate || 0}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Intervention Completion
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Care Gaps and Interventions Details - Tabbed View */}
          <Box sx={{ mt: 3 }}>
            <Tabs
              value={detailsTab}
              onChange={handleDetailsTabChange}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab 
                label={`Care Gaps (${careGaps?.length || 0})`}
                icon={<WarningIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab 
                label={`Interventions (${interventions?.length || 0})`}
                icon={<CheckCircleIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            {/* Care Gaps Tab */}
            {detailsTab === 0 && (
              <Box>
                {careGaps && careGaps.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Patient</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Gap / Category</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Provider</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {careGaps.map((gap, index) => {
                          const dueDate = gap.dueDate || gap.date;
                          const isOverdue = dueDate && new Date(dueDate) < new Date();
                          return (
                            <TableRow key={gap.id || index} hover>
                              <TableCell>{gap.patient || gap.patientName || gap.patientId || 'Unknown'}</TableCell>
                              <TableCell>{gap.gap || gap.category || gap.description || 'N/A'}</TableCell>
                              <TableCell>{dueDate || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={isOverdue ? 'Overdue' : 'Pending'}
                                  size="small"
                                  color={isOverdue ? 'error' : 'warning'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{gap.provider || 'Unassigned'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No care gaps data available.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Interventions Tab */}
            {detailsTab === 1 && (
              <Box>
                {interventions && interventions.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Patient</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Intervention</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {interventions.map((interv, index) => (
                          <TableRow key={interv.id || index} hover>
                            <TableCell>{interv.patient || interv.patientName || interv.patientId || 'Unknown'}</TableCell>
                            <TableCell>{interv.intervention || interv.description || 'N/A'}</TableCell>
                            <TableCell>{interv.type || 'N/A'}</TableCell>
                            <TableCell>{interv.date || interv.createdAt || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={interv.status || 'Unknown'} 
                                size="small" 
                                color={
                                  interv.status === 'completed' || interv.status === 'Completed' 
                                    ? 'success' 
                                    : interv.status === 'in_progress' || interv.status === 'In Progress'
                                    ? 'info'
                                    : 'warning'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No interventions data available.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </ModernCard>
    </Box>
  );
});
